import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Clapperboard, Camera, MonitorPlay, Film, 
  Sparkles, Zap, ArrowRight, Play, Star,
  Award, Users, BookOpen, AlertTriangle, Eye,
  Heart, Calendar, Music, Video, X, ShieldAlert, PhoneCall
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import Process from "../components/Process";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";
import FilmWorkshop from "../components/FilmWorkshop";

import AnimatedCounter from "../components/ui/AnimatedCounter";

const filmSteps = [
  {
    step: "01",
    title: "Discovery and Strategy",
    desc: "We start by understanding your brand, goals, and target audience. This helps us create a video strategy that aligns with your overall marketing objectives.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "02",
    title: "Creative Development",
    desc: "Our creative team brainstorms and develops concepts that bring your vision to life. We script, storyboard, and plan every detail to ensure a seamless production process.",
    image: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "Production",
    desc: "Using the latest technology and industry best practices, we film and produce high-quality videos. Our team handles everything from shooting to editing, ensuring a professional final product.",
    image: "https://images.unsplash.com/photo-1611540881474-bf7c8731bfd2?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "Post-Production",
    desc: "In post-production, we refine your video with editing, sound design, and visual effects. We also optimize the video for various social media platforms.",
    image: "https://images.unsplash.com/photo-1627244714766-94dab62ed964?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "05",
    title: "Distribution and Optimization",
    desc: "We assist with the distribution of your video content, ensuring it reaches your target audience on the right platforms. We also provide analytics to track performance and optimize future campaigns.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
  }
];

const FilmMaking = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    window.lenis = lenis;
    return () => {
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Cinematic Film Making" 
        description="High-end film production services. We combine narrative storytelling with cinematic technical excellence."
      />
      {/* <CustomCursor /> */}
      <div className="grain" />
      <Navbar />

      <main>
        <SectionStepper 
          sections={[
            { id: "top", label: "Hero" },
            { id: "stats", label: "Stats" },
            { id: "featured-1930", label: "1930 Film" },
            { id: "featured-two-wombs", label: "Two Wombs" },
            { id: "capabilities", label: "Capabilities" },
            { id: "process", label: "Process" },
            { id: "workshop", label: "Workshop" }
          ]} 
        />
        {/* Hero */}
        <section id="top" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
          <motion.div
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.55 }}
            transition={{ duration: 1.5 }}
            className="w-full h-full"
          >
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              preload="auto"
              className="w-full h-full object-cover grayscale brightness-75"
            >
              <source src="https://videos.pexels.com/video-files/34550102/14640321_1440_2560_50fps.mp4" type="video/mp4" />
            </video>
          </motion.div>
        </div>

        <div className="container-xl w-full relative z-20 px-6">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-5xl"
          >
            <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
              Cinematic Narrative
            </span>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight balance">
              Stories That <br />
              <span className="italic font-serif-accent gold-text">Move</span> Hearts
            </h1>
            <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
              We don't just record video; we craft cinematic experiences. From narrative features to high-impact commercials, our lenses capture the soul of every story.
            </p>
            <div className="flex flex-col md:flex-row items-start justify-start gap-6">
              <Link to="/contact" className="btn-primary">
                Start Your Film <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <a href="#workshop" className="btn-ghost">
                Enter Workshop
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats/Showreel teaser */}
      <section id="stats" className="py-24 bg-[var(--ink)] border-y border-[var(--rule)] relative overflow-hidden">
        <div className="container-xl px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { label: "Films Produced", value: "85", suffix: "+" },
              { label: "Nominations", value: "12", suffix: "" },
              { label: "Global Locations", value: "20", suffix: "+" },
              { label: "Resolution", value: "8", suffix: "K" }
            ].map((stat, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="group relative p-6 md:p-10 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[var(--gold)]/40 transition-all duration-700 overflow-hidden flex flex-col items-center text-center"
                style={{ backdropFilter: "blur(20px)" }}
              >
                <div className="relative z-10 w-full">
                  <div className="flex items-baseline gap-1 justify-center">
                    <div className="text-[clamp(28px,6vw,48px)] font-impact leading-none text-[var(--gold)]">
                      <AnimatedCounter value={parseInt(stat.value)} suffix={stat.suffix} />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col items-center gap-3">
                    <div className="w-8 h-px bg-[var(--gold)]/30 group-hover:w-12 transition-all" />
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--muted)] group-hover:text-[var(--cream)] transition-colors leading-relaxed">
                      {stat.label}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FeaturedFilm1930 />
      <FeaturedFilmTwoWombs />

      {/* Services Grid */}
      <section id="capabilities" className="py-32 relative">
        <div className="container-xl px-6">
          <Kicker index="01" label="Capabilities" />
          <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
            Video Production <span className="italic gold-text">Services</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Zap className="w-8 h-8" />,
                title: "Promotional Videos",
                image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=800",
                features: [
                  { headline: "Engage and Convert", body: "Our promotional videos are designed to grab attention and convert viewers into customers. We create short, compelling videos that highlight your product features, benefits, and unique selling points." },
                  { headline: "Platform Optimization", body: "Each video is tailored to fit the requirements and best practices of different social media platforms, ensuring maximum reach and engagement." }
                ]
              },
              {
                icon: <Sparkles className="w-8 h-8" />,
                title: "Branding Videos",
                image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&q=80&w=800",
                features: [
                  { headline: "Tell Your Story", body: "We create powerful branding videos that communicate your brand's mission, values, and identity. These videos help build a strong emotional connection with your audience." },
                  { headline: "Consistent Branding", body: "From concept to execution, we ensure your branding videos reflect your brand's style and tone consistently across all channels." }
                ]
              },
              {
                icon: <MonitorPlay className="w-8 h-8" />,
                title: "Social Media Campaigns",
                image: "https://images.unsplash.com/photo-1616469829581-73993eb86b02?auto=format&fit=crop&q=80&w=800",
                features: [
                  { headline: "Creative Concepts", body: "We develop innovative video concepts that align with your marketing goals and resonate with your target audience." },
                  { headline: "High-Quality Production", body: "Our state-of-the-art equipment and professional crew ensure that every video we produce is of the highest quality." },
                  { headline: "Viral Potential", body: "With a focus on shareability and engagement, our videos are crafted to have the potential to go viral, boosting your social media presence." }
                ]
              },
              {
                icon: <Camera className="w-8 h-8" />,
                title: "Product Demonstrations",
                image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&q=80&w=800",
                features: [
                  { headline: "Showcase Your Products", body: "Detailed and visually appealing product demonstration videos that highlight the features, usage, and benefits of your products." },
                  { headline: "Boost Sales", body: "By providing a clear and engaging product showcase, we help drive consumer interest and boost sales." }
                ]
              },
              {
                icon: <Clapperboard className="w-8 h-8" />,
                title: "Walkthrough & Corporate AV",
                image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=800",
                features: [
                  { headline: "Build Trust", body: "Walkthrough & Corporate AV Brochure videos featuring satisfied customers and success stories help build credibility and trust." },
                  { headline: "Authentic Engagement", body: "Real stories from real customers resonate well and provide authentic engagement on social media." }
                ]
              }
            ].map((s, i) => (
              <div 
                key={i} 
                className="relative overflow-hidden p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-3xl hover:border-[var(--gold)]/40 transition-all duration-500 group flex flex-col justify-between min-h-[460px]"
              >
                {/* Background image overlay - increased opacity for high visibility */}
                <div 
                  className="absolute inset-0 z-0 grayscale opacity-[0.32] group-hover:opacity-[0.45] group-hover:scale-105 transition-all duration-700 pointer-events-none"
                  style={{ 
                    backgroundImage: `url(${s.image})`, 
                    backgroundSize: 'cover', 
                    backgroundPosition: 'center' 
                  }}
                />
                
                {/* Solid dark overlay with decreased opacity so background is clearly visible */}
                <div className="absolute inset-0 bg-black/55 group-hover:bg-black/45 transition-colors duration-500 z-10 pointer-events-none" />

                <div className="relative z-20">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left drop-shadow-[0_2px_3px_rgba(0,0,0,0.9)]">{s.icon}</div>
                  <h3 className="font-display text-2xl mb-8 border-b border-[var(--rule)] pb-4 text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors drop-shadow-[0_2px_4px_rgba(0,0,0,0.95)]">{s.title}</h3>
                  <div className="space-y-6">
                    {s.features.map((feat, idx) => (
                      <div key={idx} className="space-y-2">
                        <h4 className="text-[10px] uppercase tracking-widest text-[var(--gold)] font-impact drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{feat.headline}</h4>
                        <p className="text-[#e4e2dd] text-[13px] leading-relaxed font-body font-medium drop-shadow-[0_2px_3px_rgba(0,0,0,0.95)]">{feat.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Process steps={filmSteps} title="The Filming Process" kicker={{ index: "02", label: "The Blueprint" }} />

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-[var(--ink)] border-t border-[var(--rule)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,169,97,0.02)_0%,transparent_70%)] pointer-events-none" />
        <div className="container-xl px-6 relative z-10 text-center max-w-4xl mx-auto">
          <span className="text-[var(--gold)] uppercase font-impact tracking-[0.3em] text-xs block mb-4">START YOUR CHAPTER</span>
          <h2 className="font-display text-5xl md:text-7xl mb-8 leading-tight text-[var(--cream)] balance">
            Let’s Create Something <span className="italic gold-text">Extraordinary</span>
          </h2>
          <p className="text-[var(--cream-muted)] text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Ready to tell your brand's narrative through stunning visual storytelling? Let's collaborate and bring your ideas to life.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-3">
            Get in touch
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <FilmWorkshop />

      </main>
      <Footer />
    </div>
  );
};

const FeaturedFilm1930 = () => {
  const [activeTab, setActiveTab] = useState("plot");
  const [posterOpen, setPosterOpen] = useState(false);

  const awards = [
    { title: "Winner — Best Film", fest: "Global India International Film Festival 2026", role: "Winner" },
    { title: "Winner — Best Short Film", fest: "Asian International Filmfare Awards 2026", role: "Winner" },
    { title: "Winner — Best Short Film", fest: "Indian Movie Awards 2026", role: "Winner" },
    { title: "Winner — Best Short Film", fest: "Silver Wings International Film Festival 2026", role: "Winner" },
    { title: "Critic’s Choice Award", fest: "The Great Britain Silver Screen Awards", role: "Winner" },
    { title: "Best Short Film", fest: "LK International Short Film Festival 2026", role: "Winner" },
    { title: "Official Selection", fest: "River Stone International Film Festival 2026", role: "Selected" },
    { title: "Official Selection", fest: "Nexgen International Short Film Festival 2026", role: "Selected" },
    { title: "Official Selection", fest: "Darbhanga International Film Festival 2026", role: "Selected" },
    { title: "Official Selection", fest: "Bengal International Short Film Festival 2026", role: "Selected" }
  ];

  return (
    <section id="featured-1930" className="py-32 relative overflow-hidden bg-[var(--ink)] border-b border-[var(--rule)]">
      {/* Background radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(201,169,97,0.03)_0%,transparent_50%)] pointer-events-none" />
      
      <div className="container-xl px-6 relative z-10">
        {/* Kicker & Heading */}
        <div className="flex items-center gap-4 mb-4">
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">01.5</span>
          <span className="w-16 h-px bg-[var(--rule)]" />
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">Featured Film</span>
        </div>
        
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Embed Player & Media Controls */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full"
          >
            <div className="relative group rounded-3xl overflow-hidden border border-[rgba(201,169,97,0.25)] shadow-[0_30px_60px_rgba(0,0,0,0.8)] bg-black/60 aspect-video p-1.5 backdrop-blur-md">
              <div className="w-full h-full rounded-[20px] overflow-hidden relative">
                <iframe
                  className="w-full h-full border-0 absolute inset-0"
                  src="https://www.youtube.com/embed/KAnKujVDjf4?si=MQRGHYCHk_vaQRkA&amp;controls=0"
                  title="1930 Film YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            {/* View counter, Poster Trigger & info badge */}
            <div className="mt-6 flex flex-wrap gap-4 items-center justify-between px-2">
              <div className="flex items-center gap-3">
                <span className="relative flex h-3.5 w-3.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500"></span>
                </span>
                <span className="text-[11px] uppercase tracking-widest text-[var(--muted)] font-impact">Public Awareness Drive</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPosterOpen(true)}
                  className="flex items-center gap-1.5 bg-white/5 border border-white/10 hover:border-[var(--gold)]/50 hover:bg-[var(--gold)]/10 px-3.5 py-1.5 rounded-full text-xs text-[var(--cream)] transition-all font-impact"
                >
                  <Eye size={12} className="text-[var(--gold)]" />
                  <span className="tracking-wider">VIEW AWARDS POSTER</span>
                </button>
                <div className="flex items-center gap-2 bg-[#ff0000]/10 border border-[#ff0000]/30 px-3.5 py-1.5 rounded-full text-xs text-white">
                  <span className="font-impact text-[#ff0000] tracking-wider">YOUTUBE</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  <span className="font-impact text-[var(--gold)] tracking-wide">2.1+ LAKH VIEWS</span>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Right Column: Narrative Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[var(--gold)] uppercase font-impact tracking-[0.25em] text-xs block mb-3">Short Film Inspired by True Events</span>
            <h2 className="font-display text-5xl sm:text-6xl mb-3 text-[var(--cream)] leading-tight">1930</h2>
            <p className="text-[var(--gold)] font-impact text-xs uppercase tracking-[0.2em] mb-3">
              A call that changed everything. A system that can save everyone.
            </p>
            <p className="text-[var(--cream-muted)] font-serif italic text-sm sm:text-base leading-relaxed mb-6 border-l-2 border-[var(--gold)]/30 pl-4">
              A hard-hitting psychological drama exposing the dark reality of cyber fraud, digital arrests, and deception.
            </p>
            
            {/* Action Tabs Selector */}
            <div className="flex flex-wrap gap-2 border-b border-[var(--rule)] pb-4 mb-6">
              {[
                { id: "plot", label: "Plot", icon: <BookOpen className="w-3.5 h-3.5" /> },
                { id: "awards", label: "Recognition & Laurels", icon: <Award className="w-3.5 h-3.5" /> },
                { id: "credits", label: "Cast & Crew", icon: <Users className="w-3.5 h-3.5" /> }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest transition-all duration-300 font-impact ${
                    activeTab === tab.id
                      ? "bg-[var(--gold)] text-[var(--ink)]"
                      : "bg-white/5 border border-white/5 text-[var(--cream-muted)] hover:border-white/10"
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Tab Contents */}
            <div className="min-h-[240px]">
              <AnimatePresence mode="wait">
                {activeTab === "plot" && (
                  <motion.div
                    key="plot"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    <p className="text-[var(--cream-muted)] text-sm leading-relaxed font-body">
                      In the quiet town of Orchha, Mansi Pathak is on the verge of starting a new chapter in Australia—unaware that a single phone call is about to change her life forever. 
                      What begins as a routine courier inquiry quickly spirals into a terrifying ordeal.
                    </p>
                    <p className="text-[var(--cream-muted)] text-sm leading-relaxed font-body">
                      Accused of smuggling, confronted with a fake warrant, and placed under a so-called “digital arrest,” Mansi finds herself trapped in a calculated web of fear and psychological manipulation.
                    </p>
                    
                    {/* Helpline Banner */}
                    <div className="p-4 rounded-2xl bg-red-950/20 border border-red-500/30 flex items-start gap-3 shadow-[0_8px_32px_rgba(239,68,68,0.08)]">
                      <PhoneCall className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <span className="font-impact text-[10px] uppercase tracking-widest text-red-400 block mb-0.5">NATIONAL CYBER CRIME HELPLINE</span>
                        <p className="text-xs font-impact text-[var(--cream)] tracking-wide leading-snug">
                          IF YOU ARE A VICTIM OF DIGITAL ARREST OR CYBER FRAUD CALL <span className="text-red-400 font-bold">1930</span>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {activeTab === "awards" && (
                  <motion.div
                    key="awards"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[260px] overflow-y-auto pr-1"
                  >
                    {awards.map((aw, i) => (
                      <div key={i} className="p-3 bg-white/[0.03] border border-white/5 rounded-xl flex items-start gap-2.5 hover:border-[var(--gold)]/30 transition-all duration-300">
                        <Award className="text-[var(--gold)] mt-0.5 flex-shrink-0" size={14} />
                        <div>
                          <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-0.5">{aw.role}</span>
                          <h4 className="text-xs font-impact text-[var(--cream)] leading-snug mb-0.5">{aw.title}</h4>
                          <span className="text-[10px] text-[var(--muted)] leading-normal block">{aw.fest}</span>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
                
                {activeTab === "credits" && (
                  <motion.div
                    key="credits"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4 text-xs text-[var(--cream-muted)]"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 border-b border-[var(--rule)]">
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-[var(--gold)] font-impact block mb-2">CREATIVE LEADERSHIP</span>
                        <ul className="space-y-1.5">
                          <li><strong>Produced By:</strong> Pawan Katyal, Shwet Sinha, Vanitaa Pande</li>
                          <li><strong>Directed By:</strong> Saandesh Nayak & Team</li>
                          <li><strong>Created By:</strong> Meraki Solutions / Wide Lens Media</li>
                        </ul>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-[var(--gold)] font-impact block mb-2">TECHNICAL CREW</span>
                        <ul className="space-y-1.5">
                          <li><strong>DOP:</strong> Mohit Puri</li>
                          <li><strong>Writer:</strong> Aparna Pasalkar</li>
                          <li><strong>Editor & Graphics:</strong> Nick Shobha Gohain</li>
                        </ul>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-[var(--gold)] font-impact block mb-1.5">STARRING CAST</span>
                      <p className="leading-relaxed">
                        Praannay Naarrayan, Rana Yashodhan Singh, Priti Kochhar, Vanitaa Pande, Soubhagya Dutt, Harshit Yadav, Nilima Mishra.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
          </motion.div>
          
        </div>
      </div>

      {/* Poster Modal */}
      <AnimatePresence>
        {posterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPosterOpen(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-xl"
          >
            <button
              onClick={() => setPosterOpen(false)}
              className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[var(--gold)] hover:text-black transition-all"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-lg max-h-[90vh] rounded-2xl overflow-hidden border border-[var(--gold)]/30 shadow-[0_0_60px_rgba(201,169,97,0.2)] bg-black"
            >
              <img
                src="/images/films/1930-awards-poster.jpg"
                alt="1930 Official Film Festival Laurels Poster"
                className="max-h-[85vh] w-auto object-contain block mx-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const FeaturedFilmTwoWombs = () => {
  const [activeTab, setActiveTab] = useState("story");
  const [posterOpen, setPosterOpen] = useState(false);
  const [trailerOpen, setTrailerOpen] = useState(false);

  return (
    <section id="featured-two-wombs" className="py-32 relative overflow-hidden bg-[#0c0a09] border-b border-[var(--rule)]">
      {/* Background warm radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_50%,rgba(235,160,130,0.04)_0%,transparent_60%)] pointer-events-none" />
      
      <div className="container-xl px-6 relative z-10">
        {/* Kicker */}
        <div className="flex items-center gap-4 mb-4">
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">01.6</span>
          <span className="w-16 h-px bg-[var(--rule)]" />
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">NEW CINEMATIC CHAPTER</span>
        </div>
        
        {/* Inverted Grid: Left Side Content, Right Side Image */}
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Narrative Details & Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[var(--gold)] uppercase font-impact tracking-[0.25em] text-xs">
                VANITAA VINS PRESENTS • A BAATON MEIN PRODUCTIONS
              </span>
            </div>

            <div className="flex items-baseline gap-4 flex-wrap mb-4">
              <h2 className="font-display text-5xl sm:text-6xl text-[var(--cream)] leading-tight">
                Two Wombs, <span className="italic font-serif-accent gold-text">One Story</span>
              </h2>
            </div>

            {/* Release Date Banner Pill */}
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 mb-6">
              <Calendar size={13} className="text-[var(--gold)]" />
              <span className="font-impact text-xs uppercase tracking-widest text-[var(--gold)]">
                RELEASING ON 24TH JULY
              </span>
            </div>

            <p className="text-[var(--cream-muted)] font-serif italic text-sm sm:text-base leading-relaxed mb-8 border-l-2 border-[var(--gold)]/40 pl-4">
              “A tender, heart-stirring narrative exploring the unspoken bonds, emotional resilience, and the sacred journey of motherhood.”
            </p>
            
            {/* Action Tabs Selector */}
            <div className="flex flex-wrap gap-2 border-b border-[var(--rule)] pb-4 mb-8">
              {[
                { id: "story", label: "Story & Premise", icon: <BookOpen className="w-3.5 h-3.5" /> },
                { id: "credits", label: "Key Credits", icon: <Users className="w-3.5 h-3.5" /> },
                { id: "music", label: "Craft & Music", icon: <Music className="w-3.5 h-3.5" /> }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] uppercase tracking-widest transition-all duration-300 font-impact ${
                    activeTab === tab.id
                      ? "bg-[var(--gold)] text-[var(--ink)]"
                      : "bg-white/5 border border-white/5 text-[var(--cream-muted)] hover:border-white/10"
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Tab Contents */}
            <div className="min-h-[220px]">
              <AnimatePresence mode="wait">
                {activeTab === "story" && (
                  <motion.div
                    key="story"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    <p className="text-[var(--cream-muted)] text-sm leading-relaxed font-body">
                      <em>Two Wombs, One Story</em> captures an intimate human journey centered on compassion, destiny, and the miraculous miracle of life. 
                      Delicately framed against the quiet beauty of unconditional devotion, the film touches the deepest chords of what it truly means to give life.
                    </p>
                    <p className="text-[var(--cream-muted)] text-sm leading-relaxed font-body">
                      Featuring warm, naturalistic cinematography and poignant character dynamics, this cinematic chapter is an ode to mothers and the shared strength that unites them.
                    </p>
                  </motion.div>
                )}
                
                {activeTab === "credits" && (
                  <motion.div
                    key="credits"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                      <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-1">PRODUCED BY</span>
                      <h4 className="text-sm font-impact text-[var(--cream)]">Vanitaa Pande</h4>
                      <p className="text-[11px] text-[var(--muted)] mt-0.5">Vanitaa Vins / Baaton Mein Productions</p>
                    </div>

                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                      <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-1">DIRECTED BY</span>
                      <h4 className="text-sm font-impact text-[var(--cream)]">Akshay Yagnik</h4>
                      <p className="text-[11px] text-[var(--muted)] mt-0.5">Director & Visual Staging</p>
                    </div>

                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                      <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-1">STORY BY</span>
                      <h4 className="text-sm font-impact text-[var(--cream)]">Chitra Katyal</h4>
                      <p className="text-[11px] text-[var(--muted)] mt-0.5">Story & Core Concept</p>
                    </div>

                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                      <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-1">WRITTEN BY</span>
                      <h4 className="text-sm font-impact text-[var(--cream)]">Roshan Ramesh Shetty</h4>
                      <p className="text-[11px] text-[var(--muted)] mt-0.5">Screenplay & Dialogues</p>
                    </div>
                  </motion.div>
                )}
                
                {activeTab === "music" && (
                  <motion.div
                    key="music"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                      <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-1">ORIGINAL MUSIC SCORE</span>
                      <h4 className="text-sm font-impact text-[var(--cream)]">Sarthak (SRTK)</h4>
                      <p className="text-[11px] text-[var(--muted)] mt-0.5">Acoustic & Emotive Film Score</p>
                    </div>

                    <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                      <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] block font-impact mb-1">CINEMATOGRAPHY</span>
                      <h4 className="text-sm font-impact text-[var(--cream)]">Jayant Sharma & Aayush Amin</h4>
                      <p className="text-[11px] text-[var(--muted)] mt-0.5">Director of Photography & Lighting</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={() => setTrailerOpen(true)}
                className="btn-primary flex items-center gap-2 shadow-lg shadow-[var(--gold)]/10 hover:shadow-[var(--gold)]/25 transition-all"
              >
                <Play size={15} fill="currentColor" />
                <span>Watch Trailer</span>
              </button>
              <button
                onClick={() => setPosterOpen(true)}
                className="btn-ghost flex items-center gap-2"
              >
                <Eye size={15} />
                <span>Enlarge Poster</span>
              </button>
              <Link to="/contact" className="btn-ghost">
                Inquire For Screenings
              </Link>
            </div>
            
          </motion.div>
          
          {/* Right Column: Image Poster (Properly positioned on the right) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full flex justify-center order-1 lg:order-2"
          >
            <div 
              onClick={() => setTrailerOpen(true)}
              className="relative group rounded-3xl overflow-hidden border border-[rgba(201,169,97,0.3)] shadow-[0_25px_60px_rgba(0,0,0,0.85)] bg-black/80 max-w-sm w-full cursor-pointer hover:border-[var(--gold)] transition-all duration-500"
            >
              {/* Image Frame */}
              <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[3/4] bg-[#110f0e]">
                <img
                  src="/images/films/two-wombs-one-story-poster.jpg"
                  alt="Two Wombs, One Story Official Film Poster"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Ambient vignette gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />

                {/* Center Play Button on Poster Hover */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-black/60 backdrop-blur-md border border-[var(--gold)]/50 flex items-center justify-center text-[var(--gold)] group-hover:scale-115 group-hover:bg-[var(--gold)] group-hover:text-black transition-all duration-300 shadow-2xl">
                    <Play size={24} fill="currentColor" className="ml-1" />
                  </div>
                </div>

                {/* Top Badge */}
                <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-md border border-white/10 px-3 py-1 rounded-full flex items-center gap-1.5">
                  <Sparkles size={10} className="text-[var(--gold)]" />
                  <span className="font-impact text-[9px] uppercase tracking-wider text-[var(--gold)]">
                    Official Trailer & Poster
                  </span>
                </div>

                {/* Bottom Overlay Info on Poster */}
                <div className="absolute bottom-4 left-4 right-4 p-3 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-between">
                  <div>
                    <span className="text-[9px] uppercase tracking-widest text-[var(--gold)] font-impact block">
                      RELEASING SOON
                    </span>
                    <span className="text-xs font-display text-[var(--cream)] font-medium">
                      24th July
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-impact text-[var(--gold)] tracking-wider uppercase">
                      Play Trailer
                    </span>
                    <div className="w-7 h-7 rounded-full bg-[var(--gold)] text-black flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play size={12} fill="currentColor" className="ml-0.5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
        </div>
      </div>

      {/* Trailer Video Player Modal */}
      <AnimatePresence>
        {trailerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setTrailerOpen(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/95 backdrop-blur-2xl"
          >
            <button
              onClick={() => setTrailerOpen(false)}
              className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[var(--gold)] hover:text-black transition-all"
              title="Close Trailer"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-[92vw] max-w-4xl aspect-video rounded-2xl overflow-hidden border border-[var(--gold)]/30 shadow-[0_0_80px_rgba(201,169,97,0.25)] bg-black"
            >
              <video
                src="/videos/two-wombs-trailer.mp4"
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain bg-black"
              >
                Your browser does not support the video tag.
              </video>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Poster Enlarge Modal */}
      <AnimatePresence>
        {posterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPosterOpen(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-xl"
          >
            <button
              onClick={() => setPosterOpen(false)}
              className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[var(--gold)] hover:text-black transition-all"
              title="Close Poster"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-lg max-h-[90vh] rounded-2xl overflow-hidden border border-[var(--gold)]/30 shadow-[0_0_60px_rgba(201,169,97,0.2)] bg-black"
            >
              <img
                src="/images/films/two-wombs-one-story-poster.jpg"
                alt="Two Wombs, One Story Official Film Poster Enlarge"
                className="max-h-[85vh] w-auto object-contain block mx-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default FilmMaking;
